import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Save, Help as HelpIcon } from '@mui/icons-material';
import { getSecuritySettings, updateSecuritySettings } from '../api';

export default function SecuritySettings() {
  const [settings, setSettings] = useState({
    auth_method: 'local',
    mfa_required: false,
    mfa_admin_required: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    const loadSettings = async () => {
      try {
        const res = await getSecuritySettings();
        if (!cancelled) setSettings(res.data);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load security settings');
          console.error(err);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadSettings();
    return () => { cancelled = true; };
  }, []);

  const handleToggle = (field) => {
    setSettings(prev => {
      const updated = { ...prev, [field]: !prev[field] };
      // All-users MFA implies admin MFA
      if (updated.mfa_required) updated.mfa_admin_required = true;
      return updated;
    });
    setSuccess('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await updateSecuritySettings({
        mfa_required: settings.mfa_required,
        mfa_admin_required: settings.mfa_admin_required,
      });
      setSuccess('Security settings saved successfully');
    } catch (err) {
      setError('Failed to save security settings');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const authMethodLabel = settings.auth_method === 'oidc'
    ? 'Entra ID (SSO)'
    : 'Local (Password)';

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto' }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Security Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure authentication and multi-factor authentication (MFA) policies for your organization.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper sx={{ mb: 3, p: 3 }}>
        {/* Authentication Method (read-only) */}
        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          Authentication Method
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" color="text.secondary">Current:</Typography>
          <Chip label={authMethodLabel} size="small" variant="outlined" color="primary" />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Contact your program administrator to change the authentication method.
        </Typography>

        <Divider sx={{ my: 2.5 }} />

        {/* MFA Policy */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">
            MFA Policy
          </Typography>
          <Tooltip title="Multi-Factor Authentication (MFA) adds a second layer of security. Users will need an authenticator app (Google Authenticator, Microsoft Authenticator, or Authy) to generate a 6-digit code each time they sign in.">
            <IconButton size="small"><HelpIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ pr: 2 }}>
            <Typography variant="body2">Require MFA for Admins</Typography>
            <Typography variant="caption" color="text.secondary">
              Admin users must set up MFA before accessing the system
            </Typography>
          </Box>
          <Switch
            checked={settings.mfa_admin_required}
            onChange={() => handleToggle('mfa_admin_required')}
            color="primary"
            disabled={settings.mfa_required}
          />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ pr: 2 }}>
            <Typography variant="body2">Require MFA for All Users</Typography>
            <Typography variant="caption" color="text.secondary">
              Every user in this organization must set up MFA. Overrides the admin-only setting
            </Typography>
          </Box>
          <Switch
            checked={settings.mfa_required}
            onChange={() => handleToggle('mfa_required')}
            color="primary"
          />
        </Box>

        <Alert severity="info" sx={{ mt: 1 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            How MFA works for users:
          </Typography>
          <Typography variant="body2" component="ol" sx={{ pl: 2, mb: 0 }}>
            <li>Users log in with their password as usual</li>
            <li>If MFA is required but not yet set up, they are prompted to configure it</li>
            <li>They scan a QR code with an authenticator app (Google Authenticator, Microsoft Authenticator, Authy)</li>
            <li>On each future login, they enter the 6-digit code from their app</li>
            <li>Backup codes are provided in case the user loses access to their authenticator app</li>
          </Typography>
        </Alert>
      </Paper>

      <Button
        variant="contained"
        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </Box>
  );
}
